import { useOutletContext } from 'react-router-dom'
import { Outlet, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Delete, Upload } from '@factorialco/f0-react/icons/app'
import {
  TrainingDetailDocumentsDeleteTrainingFileDocument,
  TrainingDetailDocumentsDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { MaterialItem, MaterialsList } from 'modules/trainings/components/MaterialsList'
import { DetailOutletContext } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

export default function Documents() {
  const { training } = useOutletContext<DetailOutletContext>()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const canEdit = training._policies?.edit.can ?? false

  const [destroyTrainingFile] = useMutation(TrainingDetailDocumentsDeleteTrainingFileDocument, {
    extractorFn: (data) => data.trainingsMutations.deleteTrainingsTrainingFile,
    awaitRefetchQueries: true,
    refetchQueries: [TrainingDetailDocumentsDocument],
  })

  const source = useGraphqlDataCollectionSource({
    document: TrainingDetailDocumentsDocument,
    search: {
      enabled: true,
    },
    mapData: (data): MaterialItem[] => {
      return data.trainings.trainingFilesConnection.nodes.map((file) => ({
        contentType: file.contentType,
        filename: file.filename,
        id: file.id,
        size: file.size,
        title: file.title,
        type: 'training' as const,
      }))
    },
    mapVariables: ({ search }) => ({
      trainingId: training.id,
      search: search,
    }),
    mapPagination: (data) => {
      return {
        total: data.trainings.trainingFilesConnection.totalCount || 0,
      }
    },
    itemUrl: (item) =>
      navigation.trainings.list.details_revamp.view.documents.previewFile.fullPath({
        id: training.id,
        fileId: item.id,
      }),
    primaryActions: () => ({
      label: i18n.t('common.upload'),
      icon: Upload,
      onClick: () => {
        navigate(
          navigation.trainings.list.details_revamp.view.documents.newFile.fullPath({
            id: training.id,
          })
        )
      },
    }),
    itemActions: (item) => {
      if (!canEdit) return []
      return [
        {
          label: i18n.t('common.delete'),
          onClick: () => {
            destroyTrainingFile({
              variables: { id: item.id },
            })
          },
          icon: Delete,
        },
      ]
    },
  })

  return (
    <ResponsivePage.Body>
      <Box gap="s16">
        <SectionHeader
          title={i18n.t(
            'trainings.trainings_revamp.training_detail.documents.section_header.title'
          )}
          description={i18n.t(
            'trainings.trainings_revamp.training_detail.documents.section_header.description'
          )}
          separator="bottom"
        />
        <MaterialsList
          source={source}
          emptyStates={{
            'no-data': {
              title: i18n.t(
                'trainings.trainings_revamp.training_detail.documents.datacollection.empty_state.no_data.title'
              ),
              description: i18n.t(
                'trainings.trainings_revamp.training_detail.documents.datacollection.empty_state.no_data.description'
              ),
            },
          }}
        />
        <Outlet />
      </Box>
    </ResponsivePage.Body>
  )
}
